/* eslint-disable react/jsx-props-no-spreading */
import React, { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { SubmitButton } from 'components';
import { Heading } from 'components/chroma';
import { PaymentState } from 'scenes/payments/types';
import { getPaymentState } from 'scenes/payments/redux/payment/selectors';
import useNextPage from 'scenes/payments/hooks';
import {
  InputQuestion,
  SegmentedControlOptions,
  SegmentedControlQuestion,
} from 'scenes/common/components/Questions';
import { PaymentMethod, PaymentWhen } from 'scenes/common/types';
import { updatePayment } from 'scenes/payments/redux/payment/paymentActions';
import { getPolicyStartAndEndDate } from 'scenes/payments/helpers/utils';
import {
  APOST_EMAIL_MSG,
  checkEmailForSingleQuote,
} from 'scenes/common/helpers/validation';
import moment from 'moment';
import { TextSmall } from 'components/chroma/extended';
import PolicySummarySection from './PolicySummarySection';
import PremiumDetail from './PremiumDetail';
import { FormGroupMarginWrapper } from './PolicyPayment.styles';

type Inputs = {
  paymentMethod: PaymentMethod;
  paymentWhen: PaymentWhen;
  contactEmailAddress?: string;
};

const CONTACT_EMAIL_ADDRESS = 'contactEmailAddress';
const ERROR_MESSAGE = 'Please enter a valid email address';
const VALID_EMAIL_PATTERN = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/;
const MIN_LENGTH = 5;
const PAY_METHOD = 'paymentMethod';

const paymentMethodOptions: SegmentedControlOptions<string, string> = [
  { label: 'Credit card', value: PaymentMethod.CreditCard },
  { label: 'Direct debit', value: PaymentMethod.DirectDebit },
];

const getDueDate = (paymentState: PaymentState) => {
  const { dates } = paymentState.policy;
  let stringDate;
  if (dates?.effective) {
    const dueDate = new Date(dates.effective);
    stringDate = moment(dueDate).format('DD/MM/YYYY');
  }
  return stringDate;
};

const getPaymentWhenOptions = (paymentState: PaymentState) => {
  const paymentWhenOptions: SegmentedControlOptions<string, string> = [
    { label: 'Pay now', value: PaymentWhen.Now },
  ];

  const { dates } = paymentState.policy;

  if (dates?.effective) {
    const dueDate = new Date(dates.effective);
    const currentDate = new Date();
    if (currentDate < dueDate) {
      paymentWhenOptions.push({
        label: `Renewal due date ${getDueDate(paymentState)}`,
        value: PaymentWhen.DueDate,
      });
    }
  }

  return paymentWhenOptions;
};

const getPolicySummary = (paymentState: PaymentState) => {
  const periodItem = getPolicyStartAndEndDate(paymentState, 'DD/MM/YYYY');

  return {
    product: paymentState.product,
    policyNumber: paymentState.policy.number,
    insuranceType: paymentState.policy.otherDetails.description,
    details: paymentState.policy.otherDetails.risk,
    period: `${periodItem.startDate} to ${periodItem.endDate}`,
  };
};

export const getPremiumInfo = (paymentState: PaymentState) => {
  const premium = paymentState.premiums?.[0];

  const total = premium?.total || 0;
  const gst = premium?.gst || 0;
  const sd = premium?.sd || 0;
  const fsl = premium?.fsl || 0;

  return {
    price: total.toString(),
    gst: gst.toString(),
    sd: sd.toString(),
    fsl: fsl.toString(),
  };
};

export const PolicyPayment: FunctionComponent = () => {
  const nextPage = useNextPage();
  const paymentState = useSelector(getPaymentState);
  const dispatch = useDispatch();

  const onlySupportPayNow =
    getPaymentWhenOptions(paymentState).length === 1 ||
    paymentState.policy.renewalStatus === 'Dishonour';

  const methods = useForm<Inputs>({
    mode: 'onChange',
  });

  const {
    formState: { isValid },
    handleSubmit,
    errors,
    control,
    // watch,
  } = methods;

  const getContactAddress = (state: PaymentState) =>
    state.payment?.contactEmailAddress ?? '';

  const getPaymentMethod = (state: PaymentState) =>
    state.payment?.paymentMethod ?? '';

  const getPaymentWhen = (state: PaymentState) =>
    state.payment?.paymentWhen ?? '';

  const onSubmit = (data: Inputs) => {
    const payment = {
      paymentMethod: data.paymentMethod,
      paymentWhen: onlySupportPayNow ? PaymentWhen.Now : data.paymentWhen,
      contactEmailAddress: data.contactEmailAddress,
      receipt: '',
    };

    dispatch(updatePayment({ payment }));
    nextPage();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Heading size={1} textAlign="center">
        Make a payment
      </Heading>
      <PremiumDetail {...getPremiumInfo(paymentState)} />
      <TextSmall margin={{ top: 4, bottom: 0 }}>
        * Includes GST and government charges.
      </TextSmall>
      <PolicySummarySection {...getPolicySummary(paymentState)} />
      {!onlySupportPayNow && (
        <SegmentedControlQuestion
          options={getPaymentWhenOptions(paymentState)}
          stateKey="paymentWhen"
          question="When would you like to pay?"
          defaultValue={getPaymentWhen(paymentState)}
          control={control}
          errors={errors}
        />
      )}
      <FormGroupMarginWrapper>
        <InputQuestion
          errors={errors}
          control={control}
          stateKey={CONTACT_EMAIL_ADDRESS}
          defaultValue={getContactAddress(paymentState)}
          autoCompleteName="email"
          ariaRequired
          validationRules={{
            required: false,
            minLength: MIN_LENGTH,
            pattern: {
              value: VALID_EMAIL_PATTERN,
              message: ERROR_MESSAGE,
            },
            validate: (email: string): string | boolean => {
              if (checkEmailForSingleQuote(email)) {
                return APOST_EMAIL_MSG;
              }
              return true;
            },
          }}
          question="Email for payment confirmation (optional)"
          placeholder="e.g. name@company.com"
          nonClickableHelpText="The email address you provide here will not override the email address currently linked to your policy. It is only used to send a payment confirmation."
          ariaDescribedBy="help-text"
        />
      </FormGroupMarginWrapper>
      <SegmentedControlQuestion
        options={paymentMethodOptions}
        stateKey={PAY_METHOD}
        question="Payment method"
        defaultValue={getPaymentMethod(paymentState)}
        control={control}
        errors={errors}
      />
      <SubmitButton enabled={isValid}>Next</SubmitButton>
    </form>
    // </Container>
  );
};

export default PolicyPayment;
